import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSectionSelect,
  EducationStaffSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type SessionDetail = {
  id: string;
  code?: string;
  name?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  record_version?: number;
  terms?: Array<{ id: string; name: string; start_date: string; end_date: string; record_version?: number }>;
  sections?: Array<{
    id: string;
    name: string;
    capacity: number;
    enrolled_count?: number;
    seats_available?: number;
    grade_level_id?: string;
    grade_code?: string;
    grade_name?: string;
    record_version?: number;
  }>;
  offerings?: Array<{
    id: string;
    subject_code?: string;
    subject_name?: string;
    grade_code?: string;
    grade_name?: string;
    group_name?: string;
    is_elective?: number | boolean;
    record_version?: number;
  }>;
  teachers?: Array<{
    id: string;
    section_name?: string;
    grade_code?: string;
    subject_code?: string;
    subject_name?: string;
    employee_code?: string;
    first_name?: string;
    last_name?: string;
  }>;
  grades?: Array<{ id: string; code: string; name: string; sequence_no: number; record_version?: number }>;
  policy?: { pack_code?: string; version?: string; policy_status?: string } | null;
  summary?: {
    term_count: number;
    section_count: number;
    offering_count: number;
    teacher_assignment_count: number;
    grade_count: number;
    grades_without_sections: Array<{ id: string; code: string; name: string }>;
    seats_total: number;
    enrolled_total: number;
  };
};

const SECTION_NAME_PRESETS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function AcademicSessionDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const activeTab = params.get('panel') || 'overview';

  const [termForm] = Form.useForm();
  const [gradeForm] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [subjectForm] = Form.useForm();
  const [offeringForm] = Form.useForm();
  const [teacherForm] = Form.useForm();
  const [sectionEdit, setSectionEdit] = useState<NonNullable<SessionDetail['sections']>[number] | null>(null);
  const [termEdit, setTermEdit] = useState<NonNullable<SessionDetail['terms']>[number] | null>(null);
  const [gradeEdit, setGradeEdit] = useState<NonNullable<SessionDetail['grades']>[number] | null>(null);
  const [offeringEdit, setOfferingEdit] = useState<NonNullable<SessionDetail['offerings']>[number] | null>(null);
  const [sectionEditForm] = Form.useForm<{ name: string; capacity: number; grade_level_id: string }>();
  const [termEditForm] = Form.useForm<{ name: string; start_date: string; end_date: string }>();
  const [gradeEditForm] = Form.useForm<{ code: string; name: string; sequence_no: number }>();
  const [offeringEditForm] = Form.useForm<{ is_elective?: boolean }>();

  const detail = useQuery({
    queryKey: ['education-academic-session-detail', id],
    enabled: Boolean(id),
    queryFn: () => api.get<SessionDetail>(`/education/academic/sessions/${id}`),
  });

  const session = detail.data?.data;
  const summary = session?.summary;
  const closed = session?.status === 'closed';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-academic-session-detail', id] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-sections'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-sections'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-grades'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-subjects'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-sessions'] });
  };

  const onError = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const activate = useMutation({
    mutationFn: () => api.post(`/education/academic/sessions/${id}/activate`, { record_version: session?.record_version }),
    onSuccess: () => {
      message.success(t('sessionActivated', { defaultValue: 'Session activated' }));
      invalidate();
    },
    onError,
  });

  const closeSession = useMutation({
    mutationFn: () => api.post(`/education/academic/sessions/${id}/close`, { record_version: session?.record_version }),
    onSuccess: () => {
      message.success(t('sessionClosed', { defaultValue: 'Session closed' }));
      invalidate();
    },
    onError,
  });

  const term = useMutation({
    mutationFn: (values: { name: string; start_date: string; end_date: string }) =>
      api.post(`/education/academic/sessions/${id}/terms`, values),
    onSuccess: () => {
      message.success(t('termSaved'));
      termForm.resetFields();
      invalidate();
    },
    onError,
  });

  const grade = useMutation({
    mutationFn: (values: { code: string; name: string; sequence_no: number }) => api.post('/education/academic/grades', values),
    onSuccess: () => {
      message.success(t('saved'));
      gradeForm.resetFields();
      invalidate();
    },
    onError,
  });

  const section = useMutation({
    mutationFn: (values: { grade_level_id: string; name: string; capacity: number }) =>
      api.post('/education/academic/sections', { ...values, session_id: id }),
    onSuccess: () => {
      message.success(t('sectionCreated', { defaultValue: 'Section created' }));
      sectionForm.resetFields();
      invalidate();
    },
    onError,
  });

  const bulk = useMutation({
    mutationFn: (values: { grade_level_ids: string[]; names: string[]; capacity: number }) =>
      api.post('/education/academic/sections/bulk', { ...values, session_id: id }),
    onSuccess: (result) => {
      const data = result.data as { created_count?: number; skipped_count?: number };
      message.success(
        t('bulkSectionsCreated', {
          defaultValue: 'Created {{created}} sections ({{skipped}} skipped)',
          created: data?.created_count ?? 0,
          skipped: data?.skipped_count ?? 0,
        })
      );
      bulkForm.resetFields();
      invalidate();
    },
    onError,
  });

  const subject = useMutation({
    mutationFn: (values: { code: string; name: string; hod_staff_link_id?: string }) =>
      api.post('/education/academic/subjects', {
        code: values.code,
        name: values.name,
        hod_staff_link_id: values.hod_staff_link_id || null,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      subjectForm.resetFields();
      invalidate();
    },
    onError,
  });

  const offering = useMutation({
    mutationFn: (values: { grade_level_id: string; subject_id: string; is_elective?: boolean }) =>
      api.post('/education/academic/offerings', { ...values, session_id: id }),
    onSuccess: () => {
      message.success(t('offeringCreated', { defaultValue: 'Subject offering created' }));
      offeringForm.resetFields();
      invalidate();
    },
    onError,
  });

  const teacher = useMutation({
    mutationFn: (values: { section_id: string; subject_id: string; staff_link_id: string }) =>
      api.post('/education/academic/section-teachers', values),
    onSuccess: () => {
      message.success(t('teacherAssigned', { defaultValue: 'Teacher assigned' }));
      teacherForm.resetFields();
      invalidate();
    },
    onError,
  });

  const updateSection = useMutation({
    mutationFn: (values: { id: string; name: string; capacity: number; grade_level_id: string; record_version: number }) =>
      api.put(`/education/academic/sections/${values.id}`, {
        name: values.name,
        capacity: values.capacity,
        grade_level_id: values.grade_level_id,
        record_version: values.record_version,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      setSectionEdit(null);
      invalidate();
    },
    onError,
  });

  const deleteSection = useMutation({
    mutationFn: (sectionId: string) => api.delete(`/education/academic/sections/${sectionId}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidate();
    },
    onError,
  });

  const updateTerm = useMutation({
    mutationFn: (values: { id: string; name: string; start_date: string; end_date: string; record_version: number }) =>
      api.put(`/education/academic/terms/${values.id}`, {
        name: values.name,
        start_date: values.start_date,
        end_date: values.end_date,
        record_version: values.record_version,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      setTermEdit(null);
      invalidate();
    },
    onError,
  });

  const deleteTerm = useMutation({
    mutationFn: (termId: string) => api.delete(`/education/academic/terms/${termId}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidate();
    },
    onError,
  });

  const updateGrade = useMutation({
    mutationFn: (values: { id: string; code: string; name: string; sequence_no: number; record_version: number }) =>
      api.put(`/education/academic/grades/${values.id}`, {
        code: values.code,
        name: values.name,
        sequence_no: values.sequence_no,
        record_version: values.record_version,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      setGradeEdit(null);
      invalidate();
    },
    onError,
  });

  const deleteGrade = useMutation({
    mutationFn: (gradeId: string) => api.delete(`/education/academic/grades/${gradeId}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidate();
    },
    onError,
  });

  const updateOffering = useMutation({
    mutationFn: (values: { id: string; is_elective?: boolean; record_version: number }) =>
      api.put(`/education/academic/offerings/${values.id}`, {
        is_elective: values.is_elective,
        record_version: values.record_version,
      }),
    onSuccess: () => {
      message.success(t('saved'));
      setOfferingEdit(null);
      invalidate();
    },
    onError,
  });

  const deleteOffering = useMutation({
    mutationFn: (offeringId: string) => api.delete(`/education/academic/offerings/${offeringId}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidate();
    },
    onError,
  });

  const unassignTeacher = useMutation({
    mutationFn: (assignmentId: string) => api.delete(`/education/academic/section-teachers/${assignmentId}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidate();
    },
    onError,
  });

  const gradeOptions = useMemo(
    () => (session?.grades ?? []).map((row) => ({ value: row.id, label: `${row.code} — ${row.name}` })),
    [session?.grades]
  );

  useEffect(() => {
    if (!sectionEdit) {
      sectionEditForm.resetFields();
      return;
    }
    sectionEditForm.setFieldsValue({
      name: sectionEdit.name,
      capacity: sectionEdit.capacity,
      grade_level_id: sectionEdit.grade_level_id || undefined,
    });
  }, [sectionEdit, sectionEditForm]);

  const setPanel = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'overview') next.delete('panel');
    else next.set('panel', key);
    setParams(next, { replace: true });
  };

  return (
    <EducationPageChrome
      title={session?.name || t('sessionDetail')}
      description={t('sessionDetailIntro', {
        defaultValue:
          'Operate one academic year: terms, grades, class sections, subject offerings, and section teachers. Enrollment allocation depends on sections created here.',
      })}
      tags={
        <Space size={4} wrap>
          {session?.code ? <Tag>{session.code}</Tag> : null}
          {session?.status ? <Tag color={session.status === 'active' ? 'green' : session.status === 'closed' ? 'default' : 'orange'}>{session.status}</Tag> : null}
        </Space>
      }
      extra={
        <Space wrap>
          {session?.status === 'draft' || session?.status === 'planned' ? (
            <Button type="primary" loading={activate.isPending} onClick={() => activate.mutate()}>
              {t('activateSession', { defaultValue: 'Activate session' })}
            </Button>
          ) : null}
          {session?.status === 'active' ? (
            <Button danger loading={closeSession.isPending} onClick={() => closeSession.mutate()}>
              {t('closeSession', { defaultValue: 'Close session' })}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/academic')}>{t('backToAcademic')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={detail.isLoading}>
          <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered>
            <Descriptions.Item label={t('startDate')}>{session?.start_date || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('endDate')}>{session?.end_date || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('policy', { defaultValue: 'Policy' })}>
              {session?.policy?.pack_code
                ? `${session.policy.pack_code} ${session.policy.version || ''} (${session.policy.policy_status || '—'})`
                : t('noPolicyAssigned', { defaultValue: 'No policy assigned' })}
            </Descriptions.Item>
            <Descriptions.Item label={t('terms', { defaultValue: 'Terms' })}>{summary?.term_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label={t('sectionsCount', { defaultValue: 'Sections' })}>{summary?.section_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label={t('offerings', { defaultValue: 'Offerings' })}>{summary?.offering_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label={t('teacherAssignments', { defaultValue: 'Teacher assignments' })}>
              {summary?.teacher_assignment_count ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('capacity')}>
              {summary ? `${summary.enrolled_total} / ${summary.seats_total}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('gradesWithoutSections', { defaultValue: 'Grades without sections' })}>
              {(summary?.grades_without_sections?.length ?? 0) > 0
                ? summary!.grades_without_sections.map((g) => g.code).join(', ')
                : t('none', { defaultValue: 'None' })}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {closed ? (
          <Alert type="warning" showIcon message={t('sessionClosedReadonly', { defaultValue: 'This session is closed. Configuration stays read-only.' })} />
        ) : null}

        {(summary?.grades_without_sections?.length ?? 0) > 0 && !closed ? (
          <Alert
            type="info"
            showIcon
            message={t('setupHintSections', {
              defaultValue: 'Some grades have no sections yet. Use Bulk create on the Sections tab for A–F across selected grades.',
            })}
          />
        ) : null}

        <Tabs
          activeKey={activeTab}
          onChange={setPanel}
          items={[
            {
              key: 'overview',
              label: t('overview', { defaultValue: 'Overview' }),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title={t('terms', { defaultValue: 'Terms' })}>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={session?.terms ?? []}
                      locale={{ emptyText: t('noTermsYet', { defaultValue: 'No terms yet — add Term 1 / Term 2 below.' }) }}
                      columns={[
                        { title: t('termName'), dataIndex: 'name' },
                        { title: t('startDate'), dataIndex: 'start_date', width: 140 },
                        { title: t('endDate'), dataIndex: 'end_date', width: 140 },
                        ...(!closed
                          ? [
                              {
                                title: t('actions', { defaultValue: 'Actions' }),
                                key: 'actions',
                                width: 180,
                                render: (_: unknown, row: NonNullable<SessionDetail['terms']>[number]) => (
                                  <Space>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        termEditForm.setFieldsValue({
                                          name: row.name,
                                          start_date: row.start_date,
                                          end_date: row.end_date,
                                        });
                                        setTermEdit(row);
                                      }}
                                    >
                                      {t('edit', { defaultValue: 'Edit' })}
                                    </Button>
                                    <Popconfirm
                                      title={t('confirmDeleteTerm', { defaultValue: 'Delete this term?' })}
                                      onConfirm={() => deleteTerm.mutate(row.id)}
                                    >
                                      <Button size="small" danger loading={deleteTerm.isPending}>
                                        {t('delete', { defaultValue: 'Delete' })}
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                    {!closed ? (
                      <Form form={termForm} layout="vertical" onFinish={(values) => term.mutate(values)} style={{ maxWidth: 480, marginTop: 16 }}>
                        <Form.Item name="name" label={t('termName')} rules={[{ required: true }]}>
                          <Input placeholder="Term 1" />
                        </Form.Item>
                        <Form.Item name="start_date" label={t('startDate')} rules={[{ required: true }]} extra="YYYY-MM-DD">
                          <Input />
                        </Form.Item>
                        <Form.Item name="end_date" label={t('endDate')} rules={[{ required: true }]} extra="YYYY-MM-DD">
                          <Input />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={term.isPending}>
                          {t('addTerm')}
                        </Button>
                      </Form>
                    ) : null}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'sections',
              label: t('gradesSectionsTitle', { defaultValue: 'Grades & sections' }),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title={t('sectionsForSession', { defaultValue: 'Sections in this session' })}>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={{ pageSize: 20 }}
                      dataSource={session?.sections ?? []}
                      locale={{ emptyText: t('noSectionsYet') }}
                      columns={[
                        { title: t('section'), dataIndex: 'name', width: 90 },
                        {
                          title: t('grade'),
                          key: 'grade',
                          render: (_: unknown, row: NonNullable<SessionDetail['sections']>[number]) =>
                            row.grade_name || row.grade_code || '—',
                        },
                        { title: t('capacity'), dataIndex: 'capacity', width: 90 },
                        {
                          title: t('enrolled', { defaultValue: 'Enrolled' }),
                          dataIndex: 'enrolled_count',
                          width: 90,
                          render: (v?: number) => v ?? 0,
                        },
                        {
                          title: t('seatsLeft', { defaultValue: 'Seats left' }),
                          dataIndex: 'seats_available',
                          width: 100,
                          render: (v?: number) => <Tag color={(v ?? 0) > 0 ? 'green' : 'red'}>{v ?? 0}</Tag>,
                        },
                        {
                          title: t('actions', { defaultValue: 'Actions' }),
                          key: 'actions',
                          width: 180,
                          render: (_: unknown, row: NonNullable<SessionDetail['sections']>[number]) =>
                            closed ? null : (
                              <Space>
                                <Button size="small" onClick={() => setSectionEdit(row)}>
                                  {t('edit', { defaultValue: 'Edit' })}
                                </Button>
                                <Popconfirm
                                  title={t('confirmDeleteSection', {
                                    defaultValue: 'Delete this section? Enrollments must be empty.',
                                  })}
                                  onConfirm={() => deleteSection.mutate(row.id)}
                                >
                                  <Button size="small" danger loading={deleteSection.isPending}>
                                    {t('delete', { defaultValue: 'Delete' })}
                                  </Button>
                                </Popconfirm>
                              </Space>
                            ),
                        },
                      ]}
                    />
                  </Card>

                  <Card size="small" title={t('grades', { defaultValue: 'Grades' })}>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={session?.grades ?? []}
                      columns={[
                        { title: t('gradeCode'), dataIndex: 'code', width: 100 },
                        { title: t('gradeName'), dataIndex: 'name' },
                        { title: t('sequence'), dataIndex: 'sequence_no', width: 100 },
                        ...(!closed
                          ? [
                              {
                                title: t('actions', { defaultValue: 'Actions' }),
                                key: 'actions',
                                width: 180,
                                render: (_: unknown, row: NonNullable<SessionDetail['grades']>[number]) => (
                                  <Space>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        gradeEditForm.setFieldsValue({
                                          code: row.code,
                                          name: row.name,
                                          sequence_no: row.sequence_no,
                                        });
                                        setGradeEdit(row);
                                      }}
                                    >
                                      {t('edit', { defaultValue: 'Edit' })}
                                    </Button>
                                    <Popconfirm
                                      title={t('confirmDeleteGrade', {
                                        defaultValue: 'Delete this grade? Sections and offerings must be removed first.',
                                      })}
                                      onConfirm={() => deleteGrade.mutate(row.id)}
                                    >
                                      <Button size="small" danger loading={deleteGrade.isPending}>
                                        {t('delete', { defaultValue: 'Delete' })}
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Card>

                  {!closed ? (
                    <>
                      <Card size="small" title={t('bulkCreateSections', { defaultValue: 'Bulk create sections (large school)' })}>
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message={t('bulkCreateHint', {
                            defaultValue: 'Create the same section names (A–F) across many grades in one step. Existing names are skipped.',
                          })}
                        />
                        <Form
                          form={bulkForm}
                          layout="vertical"
                          initialValues={{ names: ['A', 'B', 'C'], capacity: 40 }}
                          onFinish={(values) => bulk.mutate(values)}
                          style={{ maxWidth: 640 }}
                        >
                          <Form.Item name="grade_level_ids" label={t('grades', { defaultValue: 'Grades' })} rules={[{ required: true }]}>
                            <Select mode="multiple" options={gradeOptions} placeholder={t('selectGrades', { defaultValue: 'Select grades' })} />
                          </Form.Item>
                          <Form.Item name="names" label={t('sectionNames', { defaultValue: 'Section names' })} rules={[{ required: true }]}>
                            <Select mode="tags" options={SECTION_NAME_PRESETS.map((value) => ({ value, label: value }))} />
                          </Form.Item>
                          <Form.Item name="capacity" label={t('capacity')} rules={[{ required: true }]}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                          </Form.Item>
                          <Button type="primary" htmlType="submit" loading={bulk.isPending}>
                            {t('bulkCreateSections', { defaultValue: 'Bulk create sections' })}
                          </Button>
                        </Form>
                      </Card>

                      <Card size="small" title={t('addSection')}>
                        <Form form={sectionForm} layout="vertical" onFinish={(values) => section.mutate(values)} style={{ maxWidth: 480 }}>
                          <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
                            <EducationGradeSelect />
                          </Form.Item>
                          <Form.Item name="name" label={t('sectionName')} rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                          <Form.Item name="capacity" label={t('capacity')} rules={[{ required: true }]} initialValue={40}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                          </Form.Item>
                          <Button htmlType="submit" loading={section.isPending}>
                            {t('addSection')}
                          </Button>
                        </Form>
                      </Card>

                      <Card size="small" title={t('addGrade')}>
                        <Form form={gradeForm} layout="vertical" onFinish={(values) => grade.mutate(values)} style={{ maxWidth: 480 }}>
                          <Form.Item name="code" label={t('gradeCode')} rules={[{ required: true }]}>
                            <Input placeholder="IX" />
                          </Form.Item>
                          <Form.Item name="name" label={t('gradeName')} rules={[{ required: true }]}>
                            <Input placeholder="Class IX" />
                          </Form.Item>
                          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                          </Form.Item>
                          <Button htmlType="submit" loading={grade.isPending}>
                            {t('addGrade')}
                          </Button>
                        </Form>
                      </Card>
                    </>
                  ) : null}
                </Space>
              ),
            },
            {
              key: 'subjects',
              label: t('subjectsOfferingsTitle', { defaultValue: 'Subjects & offerings' }),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title={t('offeringsForSession', { defaultValue: 'Offerings for this session' })}>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={{ pageSize: 20 }}
                      dataSource={session?.offerings ?? []}
                      locale={{
                        emptyText: t('noOfferingsYet', {
                          defaultValue: 'No offerings yet — add subjects to the catalogue, then offer them per grade.',
                        }),
                      }}
                      columns={[
                        { title: t('grade'), dataIndex: 'grade_code', width: 100 },
                        { title: t('subjectCode'), dataIndex: 'subject_code', width: 120 },
                        { title: t('subjectName'), dataIndex: 'subject_name' },
                        {
                          title: t('elective', { defaultValue: 'Elective' }),
                          dataIndex: 'is_elective',
                          width: 100,
                          render: (v: number | boolean) => (v ? <Tag color="blue">{t('yes', { defaultValue: 'Yes' })}</Tag> : '—'),
                        },
                        { title: t('subjectGroup', { defaultValue: 'Group' }), dataIndex: 'group_name', width: 140 },
                        ...(!closed
                          ? [
                              {
                                title: t('actions', { defaultValue: 'Actions' }),
                                key: 'actions',
                                width: 180,
                                render: (_: unknown, row: NonNullable<SessionDetail['offerings']>[number]) => (
                                  <Space>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        offeringEditForm.setFieldsValue({ is_elective: Boolean(row.is_elective) });
                                        setOfferingEdit(row);
                                      }}
                                    >
                                      {t('edit', { defaultValue: 'Edit' })}
                                    </Button>
                                    <Popconfirm
                                      title={t('confirmDeleteOffering', {
                                        defaultValue: 'Delete this offering? Teacher assignments must be removed first.',
                                      })}
                                      onConfirm={() => deleteOffering.mutate(row.id)}
                                    >
                                      <Button size="small" danger loading={deleteOffering.isPending}>
                                        {t('delete', { defaultValue: 'Delete' })}
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Card>
                  {!closed ? (
                    <>
                      <Card size="small" title={t('addOffering', { defaultValue: 'Offer subject for a grade' })}>
                        <Form form={offeringForm} layout="vertical" onFinish={(values) => offering.mutate(values)} style={{ maxWidth: 480 }}>
                          <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
                            <EducationGradeSelect />
                          </Form.Item>
                          <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
                            <EducationSubjectSelect />
                          </Form.Item>
                          <Form.Item name="is_elective" valuePropName="checked">
                            <Checkbox>{t('elective', { defaultValue: 'Elective' })}</Checkbox>
                          </Form.Item>
                          <Button type="primary" htmlType="submit" loading={offering.isPending}>
                            {t('addOffering', { defaultValue: 'Offer subject for a grade' })}
                          </Button>
                        </Form>
                      </Card>
                      <Card size="small" title={t('addSubject')}>
                        <Form form={subjectForm} layout="vertical" onFinish={(values) => subject.mutate(values)} style={{ maxWidth: 480 }}>
                          <Form.Item name="code" label={t('subjectCode')} rules={[{ required: true }]}>
                            <Input placeholder="ENG" />
                          </Form.Item>
                          <Form.Item name="name" label={t('subjectName')} rules={[{ required: true }]}>
                            <Input placeholder="English" />
                          </Form.Item>
                          <Form.Item
                            name="hod_staff_link_id"
                            label={t('subjectHod', { defaultValue: 'Subject HOD' })}
                            extra={t('subjectHodHint', {
                              defaultValue: 'Optional. Choose a linked teacher as head of this subject.',
                            })}
                          >
                            <EducationStaffSelect
                              allowClear
                              placeholder={t('selectSubjectHod', { defaultValue: 'Select teacher (HOD)' })}
                            />
                          </Form.Item>
                          <Button htmlType="submit" loading={subject.isPending}>
                            {t('addSubject')}
                          </Button>
                        </Form>
                      </Card>
                    </>
                  ) : null}
                </Space>
              ),
            },
            {
              key: 'teachers',
              label: t('teacherAssignments', { defaultValue: 'Teacher assignments' }),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Alert
                    type="info"
                    showIcon
                    message={t('teacherAssignHint', {
                      defaultValue:
                        'Assign a staff link to a section for a subject that is offered to that section’s grade. Attendance marking for teachers follows these assignments.',
                    })}
                  />
                  <Card size="small" title={t('teacherAssignments', { defaultValue: 'Teacher assignments' })}>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={{ pageSize: 20 }}
                      dataSource={session?.teachers ?? []}
                      locale={{ emptyText: t('noTeacherAssignments', { defaultValue: 'No section–subject teachers assigned yet.' }) }}
                      columns={[
                        {
                          title: t('section'),
                          key: 'section',
                          render: (_: unknown, row: NonNullable<SessionDetail['teachers']>[number]) =>
                            `${row.grade_code || ''} ${row.section_name || ''}`.trim() || '—',
                        },
                        {
                          title: t('subject', { defaultValue: 'Subject' }),
                          key: 'subject',
                          render: (_: unknown, row: NonNullable<SessionDetail['teachers']>[number]) =>
                            `${row.subject_code || ''} — ${row.subject_name || ''}`.trim(),
                        },
                        {
                          title: t('teacher', { defaultValue: 'Teacher' }),
                          key: 'teacher',
                          render: (_: unknown, row: NonNullable<SessionDetail['teachers']>[number]) =>
                            [row.employee_code, [row.first_name, row.last_name].filter(Boolean).join(' ')].filter(Boolean).join(' — ') ||
                            '—',
                        },
                        ...(!closed
                          ? [
                              {
                                title: t('actions', { defaultValue: 'Actions' }),
                                key: 'actions',
                                width: 120,
                                render: (_: unknown, row: NonNullable<SessionDetail['teachers']>[number]) => (
                                  <Popconfirm
                                    title={t('confirmUnassignTeacher', {
                                      defaultValue: 'Remove this teacher assignment?',
                                    })}
                                    onConfirm={() => unassignTeacher.mutate(row.id)}
                                  >
                                    <Button size="small" danger loading={unassignTeacher.isPending}>
                                      {t('delete', { defaultValue: 'Delete' })}
                                    </Button>
                                  </Popconfirm>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Card>
                  {!closed ? (
                    <Card size="small" title={t('assignTeacher', { defaultValue: 'Assign teacher' })}>
                      <Form form={teacherForm} layout="vertical" onFinish={(values) => teacher.mutate(values)} style={{ maxWidth: 480 }}>
                        <Form.Item name="section_id" label={t('section')} rules={[{ required: true }]}>
                          <EducationSectionSelect sessionId={id} />
                        </Form.Item>
                        <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
                          <EducationSubjectSelect />
                        </Form.Item>
                        <Form.Item name="staff_link_id" label={t('teacher', { defaultValue: 'Teacher' })} rules={[{ required: true }]}>
                          <EducationStaffSelect />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={teacher.isPending}>
                          {t('assignTeacher', { defaultValue: 'Assign teacher' })}
                        </Button>
                      </Form>
                    </Card>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title={t('editSection', { defaultValue: 'Edit section' }) + (sectionEdit ? ` — ${sectionEdit.name}` : '')}
        open={Boolean(sectionEdit)}
        onCancel={() => setSectionEdit(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={sectionEditForm}
          layout="vertical"
          onFinish={(values) => {
            if (!sectionEdit) return;
            updateSection.mutate({
              id: sectionEdit.id,
              name: values.name,
              capacity: values.capacity,
              grade_level_id: values.grade_level_id,
              record_version: sectionEdit.record_version ?? 1,
            });
          }}
        >
          <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={gradeOptions}
              placeholder={t('grade')}
            />
          </Form.Item>
          <Form.Item name="name" label={t('sectionName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateSection.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editTerm', { defaultValue: 'Edit term' })}
        open={Boolean(termEdit)}
        onCancel={() => setTermEdit(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={termEditForm}
          layout="vertical"
          onFinish={(values) => {
            if (!termEdit) return;
            updateTerm.mutate({
              id: termEdit.id,
              name: values.name,
              start_date: values.start_date,
              end_date: values.end_date,
              record_version: termEdit.record_version ?? 1,
            });
          }}
        >
          <Form.Item name="name" label={t('termName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="start_date" label={t('startDate')} rules={[{ required: true }]} extra="YYYY-MM-DD">
            <Input />
          </Form.Item>
          <Form.Item name="end_date" label={t('endDate')} rules={[{ required: true }]} extra="YYYY-MM-DD">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateTerm.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editGrade', { defaultValue: 'Edit grade' })}
        open={Boolean(gradeEdit)}
        onCancel={() => setGradeEdit(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={gradeEditForm}
          layout="vertical"
          onFinish={(values) => {
            if (!gradeEdit) return;
            updateGrade.mutate({
              id: gradeEdit.id,
              code: values.code,
              name: values.name,
              sequence_no: values.sequence_no,
              record_version: gradeEdit.record_version ?? 1,
            });
          }}
        >
          <Form.Item name="code" label={t('gradeCode')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('gradeName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateGrade.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editOffering', { defaultValue: 'Edit offering' })}
        open={Boolean(offeringEdit)}
        onCancel={() => setOfferingEdit(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={offeringEditForm}
          layout="vertical"
          onFinish={(values) => {
            if (!offeringEdit) return;
            updateOffering.mutate({
              id: offeringEdit.id,
              is_elective: values.is_elective,
              record_version: offeringEdit.record_version ?? 1,
            });
          }}
        >
          <Form.Item name="is_elective" valuePropName="checked">
            <Checkbox>{t('elective', { defaultValue: 'Elective' })}</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateOffering.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
